import { useState, useRef, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { listDidGroups, getBuyPlan, executeBuyNpa, createDidGroup, type BuyPlanRow } from "../api/dids";
import { listUsers } from "../api/users";
import { useAuth } from "../auth/AuthContext";

function computeRow(r: BuyPlanRow, ceiling: number, cap: number) {
  const target = r.volume ? Math.ceil(r.volume / ceiling) : 0;
  const toBuy = Math.min(Math.max(0, target - r.usable), cap);
  const ratio = r.usable === 0 ? null : Math.round(r.volume / r.usable);
  let status = "OK";
  if (r.usable === 0) status = "NO COVERAGE";
  else if (toBuy === 0) status = "OK";
  else if (ratio !== null && ratio > ceiling * 2) status = "CRITICAL";
  else status = "OVERLOADED";
  return { ...r, target, toBuy, ratio, status };
}

export function DidAcquisitionPage() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const [usrId, setUsrId] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [ceiling, setCeiling] = useState(50);
  const [prog, setProg] = useState<{ total: number; done: number; failed: number; current: string; running: boolean } | null>(null);
  const stopRef = useRef(false);

  // Account selector: purchased DIDs are owned by the chosen user (admin sees all
  // accounts; non-admins see only their own). The group dropdown is filtered to
  // that user's caller-ID groups, and buys are routed to the group's owner.
  const { data: usersPage } = useQuery({
    queryKey: ["users-for-did-buy"],
    queryFn: () => listUsers({ per_page: 200, active_only: true }),
  });
  const users = (usersPage?.data ?? []).filter((u) => u.usr_id != null);
  // Buys are per TENANT (usr_id), but several login accounts can map to the same
  // usr_id (e.g. two staff logins on one company). Collapse to one option per
  // usr_id so the dropdown has unique keys/values — otherwise duplicate <option>
  // values made selecting those accounts ambiguous and the buy silently no-op'd.
  const tenantOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of users) if (u.usr_id != null && !m.has(u.usr_id)) m.set(u.usr_id, u.username);
    return [...m.entries()].map(([usr_id, username]) => ({ usr_id, username }));
  }, [users]);
  const { data: groups } = useQuery({ queryKey: ["did-groups-all"], queryFn: listDidGroups });

  // Default the account WITHOUT depending on the admin-only users list (which
  // 403s for non-admins → empty → previously left the page blank). The groups
  // endpoint loads for everyone and, for a non-admin, already returns only THEIR
  // groups. So: prefer the logged-in user (resolvable only when the admin users
  // list loaded AND they own a group), else the owner of the first group the API
  // returned, else the logged-in/first user.
  const groupsList = groups ?? [];
  const meUsr = users.find((u) => u.user_id === authUser?.user_id)?.usr_id ?? null;
  const meOwnsGroups = meUsr != null && groupsList.some((g) => g.created_by === meUsr);
  const defaultUsr =
    (meOwnsGroups ? meUsr : null) ??
    groupsList[0]?.created_by ??
    meUsr ??
    users[0]?.usr_id ??
    null;
  const selectedUsr = usrId ?? defaultUsr;

  const selectedName = tenantOptions.find((t) => t.usr_id === selectedUsr)?.username ?? "this user";
  const [newGroupName, setNewGroupName] = useState("");
  const createGroup = useMutation({
    // Admin creates the group ON BEHALF OF the selected tenant (owner_usr_id) so
    // the new group — and the DIDs bought into it — belong to that user, not the
    // admin. Removes the "— no caller-ID groups —" dead end.
    mutationFn: () =>
      createDidGroup(newGroupName.trim() || `${selectedName} DIDs`, selectedUsr ?? undefined),
    onSuccess: async (newId) => {
      setNewGroupName("");
      await qc.invalidateQueries({ queryKey: ["did-groups-all"] });
      setGroupId(newId);
    },
    onError: (e: unknown) => alert(`Could not create group: ${(e as Error).message}`),
  });

  const userGroups = (groups ?? []).filter((g) => g.created_by === selectedUsr);
  const gid = groupId && userGroups.some((g) => g.did_group_id === groupId)
    ? groupId
    : userGroups[0]?.did_group_id ?? null;
  const { data: plan, isLoading } = useQuery({
    // Server returns raw per-NPA volume/usable; the ceiling arg here is irrelevant
    // to the rows (the slider recomputes targets client-side), so it's fixed.
    queryKey: ["buy-plan", gid], queryFn: () => getBuyPlan(gid as number, 50, 14), enabled: gid != null,
  });

  const rows = useMemo(
    () => (plan?.rows ?? []).map((r) => computeRow(r, ceiling, plan?.per_npa_cap ?? 50)).sort((a, b) => b.volume - a.volume),
    [plan, ceiling, plan?.per_npa_cap],
  );
  const totalBuy = rows.reduce((s, r) => s + r.toBuy, 0);
  const cost = plan ? (totalBuy * plan.monthly_rate).toFixed(2) : "0";

  async function approveBuy() {
    const targets = rows.filter((r) => r.toBuy > 0);
    if (targets.length === 0) { alert("Nothing to buy at this threshold."); return; }
    const mode = plan?.dry_run
      ? "DRY-RUN: no real orders will be placed."
      : "LIVE: this places REAL carrier orders and starts monthly billing.";
    if (!confirm(`Buy ${totalBuy} number(s) across ${targets.length} area code(s)? Est. $${cost}/mo.\n\n${mode}`)) return;
    stopRef.current = false;
    setProg({ total: totalBuy, done: 0, failed: 0, current: "", running: true });
    let done = 0, failed = 0;
    for (const r of targets) {
      if (stopRef.current) break;
      setProg((p) => p && { ...p, current: r.npa });
      try {
        const res = await executeBuyNpa(gid as number, r.npa, r.toBuy);
        if (res.status === "bought" || res.status === "dry_run") done += res.bought || res.would_buy || 0;
        else failed += r.toBuy;
      } catch { failed += r.toBuy; }
      setProg((p) => p && { ...p, done, failed });
    }
    setProg((p) => p && { ...p, running: false, current: "" });
    qc.invalidateQueries({ queryKey: ["buy-plan", gid] });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">DID Acquisition Targets</h1>
        <div className="flex items-center gap-2">
          {tenantOptions.length > 1 && (
            <select className="border border-gray-300 rounded px-3 py-2 text-sm"
              value={selectedUsr ?? ""}
              onChange={(e) => { setUsrId(Number(e.target.value)); setGroupId(null); }}
              title="Account that purchased numbers are assigned to">
              {tenantOptions.map((t) => <option key={t.usr_id} value={t.usr_id}>{t.username}</option>)}
            </select>
          )}
          {userGroups.length === 0 ? (
            // No caller-ID group for this user yet — let the operator create one
            // inline (owned by the selected user) instead of hitting a dead end.
            <div className="flex items-center gap-2">
              <input
                className="border border-gray-300 rounded px-3 py-2 text-sm w-44"
                placeholder={`${selectedName} DIDs`}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                title={`Create a caller-ID group for ${selectedName}`}
              />
              <button
                onClick={() => createGroup.mutate()}
                disabled={createGroup.isPending || selectedUsr == null}
                className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm disabled:opacity-50 whitespace-nowrap"
              >
                {createGroup.isPending ? "Creating…" : "+ Create group"}
              </button>
            </div>
          ) : (
            <select className="border border-gray-300 rounded px-3 py-2 text-sm"
              value={gid ?? ""} onChange={(e) => setGroupId(Number(e.target.value))}>
              {userGroups.map((g) => <option key={g.did_group_id} value={g.did_group_id}>{g.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded p-3 mb-4">
        <label htmlFor="ceiling-slider" className="text-sm text-gray-600 whitespace-nowrap">Target max calls/DID (14d)</label>
        <input id="ceiling-slider" type="range" role="slider" min={10} max={100} step={5} value={ceiling}
          className="flex-1" onChange={(e) => setCeiling(Number(e.target.value))} />
        <span className="text-xl font-bold text-brand w-10 text-right">{ceiling}</span>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card v={totalBuy} l="DIDs to acquire" />
        <Card v={rows.filter((r) => r.toBuy > 0).length} l="NPAs over threshold" />
        <Card v={rows.reduce((s, r) => s + r.usable, 0)} l="Current DIDs" />
        <Card v={`$${cost}`} l="Est. monthly cost" />
      </div>

      {prog && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm flex items-center justify-between">
          <span>Buying {prog.done + prog.failed} / {prog.total}{prog.failed > 0 && <span className="text-red-600"> · {prog.failed} failed</span>}{prog.running && prog.current && <span className="font-mono text-gray-500"> · NPA {prog.current}</span>}</span>
          {prog.running ? <button className="text-red-700 hover:underline" onClick={() => { stopRef.current = true; }}>Stop</button> : <span className="text-green-700">Done</span>}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr><th className="px-3 py-2">NPA</th><th className="px-3 py-2">Calls/14d</th><th className="px-3 py-2">DIDs</th><th className="px-3 py-2">Ratio</th><th className="px-3 py-2">Target</th><th className="px-3 py-2">Add</th><th className="px-3 py-2">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? <tr><td colSpan={7} className="px-3 py-3 text-gray-500">Loading…</td></tr>
              : rows.map((r) => (
                <tr key={r.npa}>
                  <td className="px-3 py-2 font-bold">{r.npa} {r.state}</td>
                  <td className="px-3 py-2">{r.volume.toLocaleString()}</td>
                  <td className={`px-3 py-2 ${r.usable === 0 ? "text-red-600 font-bold" : ""}`}>{r.usable}</td>
                  <td className="px-3 py-2">{r.ratio ?? "—"}</td>
                  <td className="px-3 py-2">{r.target}</td>
                  <td className={`px-3 py-2 ${r.toBuy > 0 ? "text-orange-600 font-bold" : "text-green-600"}`}>{r.toBuy > 0 ? `+${r.toBuy}` : "✓"}</td>
                  <td className="px-3 py-2"><Badge s={r.status} /></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button onClick={approveBuy} disabled={prog?.running || totalBuy === 0}
          className="bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2 text-sm disabled:opacity-50">
          Approve &amp; buy ({totalBuy})
        </button>
        {plan && (
          plan.dry_run
            ? <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-200 text-gray-700" title="No real orders are placed">DRY-RUN</span>
            : <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800" title="Places real carrier orders and starts monthly billing">● LIVE — real orders</span>
        )}
      </div>
    </div>
  );
}

function Card({ v, l }: { v: number | string; l: string }) {
  return <div className="bg-white border border-gray-200 rounded p-3"><div className="text-2xl font-bold">{typeof v === "number" ? v.toLocaleString() : v}</div><div className="text-xs text-gray-500">{l}</div></div>;
}
function Badge({ s }: { s: string }) {
  const c = s === "CRITICAL" ? "bg-red-100 text-red-800" : s === "OVERLOADED" ? "bg-yellow-100 text-yellow-800" : s === "NO COVERAGE" ? "bg-indigo-100 text-indigo-800" : "bg-green-100 text-green-800";
  return <span className={`px-2 py-0.5 rounded text-xs ${c}`}>{s}</span>;
}
